import { Divider } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import React, { FC, useCallback, useState } from 'react';

import { useProductCheckoutLink } from '../../../../features/checkout/hooks/useProductCheckoutLink';
import { useDrawButtonLabels } from '../../../../features/draws/hooks/useDrawButtonLabels';
import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import { useClientSide } from '../../../../hooks/useClientSide';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePurchase } from '../../../../providers/PurchaseProvider';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import ProductDate from '../../ProductDate';
import { SignUpButton } from '../SignUpButton';
import { toClientUTCDateFormat } from '../utils';
import { DrawEntryButton } from './DrawEntryButton';
import { PreOrderHeader } from './PreOrderHeader';

interface Props {
  product: ProductType;
}

export const PreOrderFlow: FC<Props> = ({ product }) => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const purchasingController = usePurchase();
  const isClient = useClientSide();

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'pre-order',
    });
  }, [emitter]);

  const { isAccountInDraw, mainDraw } = useDraws();

  const isPreOrder = useIsPreOrder(mainDraw?.draw);

  const checkoutLink = useProductCheckoutLink();
  const labels = useDrawButtonLabels('mainCta', isPreOrder);

  if (!mainDraw) {
    return null;
  }

  const drawButton = (
    <DrawEntryButton
      checkoutLink={checkoutLink}
      entered={isAccountInDraw(mainDraw.draw.id)}
      label={t(labels.normal)}
      mainDraw={mainDraw}
      successLabel={t(labels.success)}
    />
  );

  const launchDate = new Date(toClientUTCDateFormat(product.launchDate));

  return (
    <>
      <div className="flex w-full flex-col space-y-6">
        <PreOrderHeader
          active
          className="w-full"
          description={t('preOrder.isOpen.description', {
            count: mainDraw.draw.allocation,
          })}
          title={t('preOrder.isOpen.title')}
        />
        <div className="flex w-full flex-col items-center space-y-3">
          {drawButton}
          <button
            className="hyperlink"
            onClick={() => {
              setShowDialog(true);
              handleHowToClick();
            }}
            type="button"
          >
            {t('subscriptionCta.preOrder.learnMoreDialog.title')}
          </button>
        </div>

        <Divider variant="dashed" />

        <div className="flex flex-col text-center">
          <p className="font-bold">
            {isInNextSixDays(launchDate)
              ? t('subscriptionDate.default.countdown')
              : t('subscriptionDate.default.date')}
          </p>
          <p>
            <ProductDate date={launchDate} />
          </p>
        </div>

        <p className="text-center text-neutral-4">
          {t('preOrder.isOpen.titleAllocation', {
            count: mainDraw.draw.allocation,
          })}
        </p>

        {isClient && (
          <SignUpButton
            isPreOrder
            labels={{ normal: t('notifyMeAtLaunch'), success: t('subscribed') }}
            product={product}
            productRef={getProductRef(purchasingController)}
            productStatus={getProductStatus(product, purchasingController)}
            showIcon
            variant="secondary"
          />
        )}
      </div>

      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.preOrder.learnMoreDialog.title')}
      >
        <p>
          <Trans i18nKey="subscriptionCta.preOrder.learnMoreDialog.paragraph1">
            When we produce a limited edition, we set aside a number for
            collectors to apply for pre-order ahead of the release day. You can
            read more on why we do this in&nbsp;
            <a className="hyperlink" href="/support/allocation" target="_blank">
              our FAQs
            </a>
            .
          </Trans>
        </p>
        <p className="my-3">
          {t('subscriptionCta.preOrder.learnMoreDialog.paragraph2')}
        </p>
        <p className="my-3">
          <Trans i18nKey="subscriptionCta.preOrder.learnMoreDialog.paragraph3">
            For each release, we reserve an allocation for pre-orders for
            different groups of collectors: some for first-time collectors, some
            for existing collectors, and so on. These groups are outlined in
            more detail on&nbsp;
            <a className="hyperlink" href="/support/allocation" target="_blank">
              this page
            </a>
            . Within these groups, we try to assign pre-orders as fairly as
            possible, giving priority to those who have missed out on editions
            they've applied for in the past, and those who apply early.
          </Trans>
        </p>
        <p className="mb-6">
          {t('subscriptionCta.preOrder.learnMoreDialog.paragraph4')}
        </p>
        {showDialog && drawButton}
      </SimpleDialog>
    </>
  );
};
